/**
 * Lets a merchant page own the shell's top bar while it's mounted.
 *
 * The 0.2 handoff puts each screen's identity in the sticky header — the wizard
 * shows "← Probá otro banner / Paso N de 4", results shows the test's dates —
 * rather than repeating a header inside the content. Pages declare what the bar
 * should say; MerchantLayout renders it and falls back to the store name.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export interface MerchantChrome {
  title: string
  subtitle?: string
  /** Route the back arrow points at. Omitted → no arrow (terminal screens). */
  backTo?: string
  backLabel?: string
  /** Append " · <store name>" to the subtitle on desktop (handoff: wizard/results). */
  showStoreOnDesktop?: boolean
}

const ChromeContext = createContext<{
  chrome: MerchantChrome | null
  setChrome: (c: MerchantChrome | null) => void
}>({ chrome: null, setChrome: () => {} })

export function MerchantChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<MerchantChrome | null>(null)
  const value = useMemo(() => ({ chrome, setChrome }), [chrome])
  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>
}

export function useMerchantChrome() {
  return useContext(ChromeContext).chrome
}

/**
 * Claim the top bar for as long as this component is mounted. Keyed on the
 * rendered strings so a step change re-publishes, but re-renders don't.
 */
export function useSetMerchantChrome(chrome: MerchantChrome | null) {
  const { setChrome } = useContext(ChromeContext)
  const key = chrome
    ? [chrome.title, chrome.subtitle, chrome.backTo, chrome.showStoreOnDesktop].join('|')
    : ''
  useEffect(() => {
    setChrome(chrome)
    return () => setChrome(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}
